import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Home, Users, Briefcase, Clock, FileText, Bell, Settings, LogOut, Menu, X,
  ChevronRight, ChevronDown, Plus, CheckCircle, AlertCircle, MapPin, Calendar, User,
  ClipboardList, BarChart3, Play, Square, RefreshCw, Search, Filter, Eye,
  Truck, Camera, Upload, Image, Wrench, Shield, FileImage, Trash2, Save,
  Building2, Sliders, Key, MessageSquare, Send, Car, Fuel, Video, HelpCircle,
  ChevronLeft, Download, Edit3, Check, ArrowLeft, Edit2, UserCheck, Archive, Receipt, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { Badge, Button, Card, Input, Modal, Select, Spinner, Textarea, FileUploadInput } from "../../shared/ui";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import PDFSignatureFieldPlacer from "../PDFSignatureFieldPlacer";

const SigningDocumentsView = ({ job, documents, users, onRefresh, canEdit, onBack }) => {
  const { user } = useAuth();
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showSignatures, setShowSignatures] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showFieldPlacer, setShowFieldPlacer] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [docForm, setDocForm] = useState({
    name: '',
    description: '',
    file_url: '',
    file_name: '',
    signing_frequency: 'first_clock_in',
    requires_supervisor: false,
    required_employee_ids: [],
    required_roles: [],
    all_employees_must_sign: false,
    signature_fields: []
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.type !== 'application/pdf') {
      toast.error("Please upload a PDF file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocForm(prev => ({
        ...prev,
        file_url: res.data.url,
        file_name: file.name
      }));
      toast.success("PDF uploaded!");
    } catch (e) {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleCreateDoc = async () => {
    if (!docForm.name || !docForm.file_url) {
      toast.error("Please provide a name and upload a PDF");
      return;
    }
    
    // Derive signer requirements from placed signature fields OR manual checkboxes
    let requires_supervisor = docForm.requires_supervisor;
    let all_employees_must_sign = docForm.all_employees_must_sign;
    const fields = docForm.signature_fields || [];
    
    if (fields.length > 0) {
      // Auto-detect from placed fields
      requires_supervisor = fields.some(f => f.signer_type === 'supervisor' || f.is_both);
      const hasEmployeeFields = fields.some(f => f.signer_type === 'employee' || f.is_both || f.signer_type === 'all_employees');
      if (hasEmployeeFields && !docForm.required_employee_ids.length && !docForm.required_roles.length) {
        all_employees_must_sign = true;
      }
    } else {
      // No fields placed - validate manual selection
      if (!requires_supervisor && !all_employees_must_sign && 
          docForm.required_employee_ids.length === 0 && docForm.required_roles.length === 0) {
        toast.error("Please specify who needs to sign or place signature fields on the PDF");
        return;
      }
    }
    
    try {
      await axios.post(`${API}/jobs/${job.id}/signing-documents`, {
        ...docForm,
        requires_supervisor,
        all_employees_must_sign,
        signature_fields: fields
      });
      
      // Also save as template if checkbox is ticked
      if (saveAsTemplate) {
        try {
          await axios.post(`${API}/signing-templates`, {
            name: docForm.name,
            description: docForm.description,
            file_url: docForm.file_url,
            file_name: docForm.file_name,
            signing_frequency: docForm.signing_frequency,
            signature_fields: fields,
            requires_supervisor,
            required_roles: docForm.required_roles,
            all_employees_must_sign
          });
          toast.success("Document added & saved as template!");
        } catch { toast.success("Document added! (Template save failed)"); }
      } else {
        toast.success("Document added!");
      }
      
      setShowAddDoc(false);
      setShowFieldPlacer(false);
      setSaveAsTemplate(false);
      setDocForm({
        name: '',
        description: '',
        file_url: '',
        file_name: '',
        signing_frequency: 'first_clock_in',
        requires_supervisor: false,
        required_employee_ids: [],
        required_roles: [],
        all_employees_must_sign: false,
        signature_fields: []
      });
      onRefresh();
    } catch (e) {
      toast.error("Failed to create document");
    }
  };

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await axios.get(`${API}/signing-templates`);
      setTemplates(res.data);
    } catch (e) {
      console.error('Failed to fetch templates:', e);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleUseTemplate = async (template) => {
    try {
      await axios.post(`${API}/jobs/${job.id}/signing-documents/from-template/${template.id}`);
      toast.success(`"${template.name}" added to job!`);
      setShowTemplatePicker(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add from template");
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/signing-templates/${templateId}`);
      toast.success("Template deleted");
      fetchTemplates();
    } catch (e) {
      toast.error("Failed to delete template");
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!confirm("Remove this signing document?")) return;
    try {
      await axios.delete(`${API}/jobs/${job.id}/signing-documents/${docId}`);
      toast.success("Document removed");
      onRefresh();
    } catch (e) {
      toast.error("Failed to remove document");
    }
  };

  const toggleEmployee = (empId) => {
    setDocForm(prev => ({
      ...prev,
      required_employee_ids: prev.required_employee_ids.includes(empId)
        ? prev.required_employee_ids.filter(id => id !== empId)
        : [...prev.required_employee_ids, empId]
    }));
  };

  const toggleRole = (role) => {
    setDocForm(prev => ({
      ...prev,
      required_roles: prev.required_roles.includes(role)
        ? prev.required_roles.filter(r => r !== role)
        : [...prev.required_roles, role]
    }));
  };

  const frequencyLabels = {
    'first_clock_in': 'First Clock-in (one time per job)',
    'daily': 'Daily (every clock-in)',
    'weekly_start': 'Weekly — Start of week (Mon)',
    'weekly_end': 'Weekly — End of week (Sun)',
    'end_of_job': 'End of Job (required to mark complete)',
    'once': 'Once Only'
  };

  const roleLabels = {
    'supervisor': 'Supervisor',
    'crane_operator': 'Crane Operator',
    'rigger': 'Rigger',
    'labourer': 'Labourer'
  };

  // Get assigned users for this job
  const assignedUsers = users.filter(u => job?.assigned_users?.includes(u.id));

  return (
    <div className="space-y-4" data-testid="signing-docs-view">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold uppercase flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Signing Documents - {job?.title}
        </h3>
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <>
              <Button size="sm" onClick={() => setShowAddDoc(true)} data-testid="add-signing-doc-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Document
              </Button>
              <Button size="sm" variant="outline" onClick={() => { fetchTemplates(); setShowTemplatePicker(true); }} data-testid="from-template-btn">
                <FileText className="w-4 h-4 mr-1" /> From Template
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" onClick={onBack}>Back</Button>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <strong>How it works:</strong> Upload PDFs that need signatures. Choose when employees need to sign 
        (e.g., first clock-in, daily) and who needs to sign (specific people, roles, or all workers). 
        Employees will be prompted to sign when they clock into this job.
      </div>

      {/* Add Document Form */}
      {showAddDoc && (
        <Card className="border-2 border-orange-200 bg-orange-50">
          <h4 className="font-bold mb-4">Add Signing Document</h4>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-slate-500 uppercase">Document Name *</label>
              <input
                value={docForm.name}
                onChange={(e) => setDocForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Site Induction Form, SWMS Acknowledgement"
                className="w-full mt-1 px-3 py-2 border-2 rounded-md"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase">Description (optional)</label>
              <input
                value={docForm.description}
                onChange={(e) => setDocForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the document"
                className="w-full mt-1 px-3 py-2 border-2 rounded-md"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase">Upload PDF *</label>
              <div className="mt-1">
                {docForm.file_url ? (
                  <div className="flex items-center gap-2 p-3 bg-white border-2 rounded-md">
                    <FileText className="w-5 h-5 text-green-600" />
                    <span className="font-medium">{docForm.file_name}</span>
                    <button onClick={() => setDocForm(prev => ({ ...prev, file_url: '', file_name: '' }))} className="ml-auto text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-orange-400 transition-colors">
                    {uploading ? (
                      <Spinner size="sm" />
                    ) : (
                      <>
                        <Upload className="w-6 h-6 text-slate-400 mr-2" />
                        <span className="text-slate-600">Click to upload PDF</span>
                      </>
                    )}
                    <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" />
                  </label>
                )}
              </div>
            </div>

            {/* PDF Signature Field Placement */}
            {docForm.file_url && (
              <div className="border-2 border-slate-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h5 className="font-bold uppercase text-sm text-slate-600">Place Signature Fields on PDF</h5>
                  <button
                    type="button"
                    onClick={() => setShowFieldPlacer(!showFieldPlacer)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                      showFieldPlacer ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                    data-testid="toggle-field-placer"
                  >
                    {showFieldPlacer ? 'Hide PDF' : 'Open PDF to Place Signatures'}
                  </button>
                </div>
                {!showFieldPlacer && docForm.signature_fields.length > 0 && (
                  <p className="text-xs text-green-600 font-medium">{docForm.signature_fields.length} signature field(s) placed</p>
                )}
                {!showFieldPlacer && docForm.signature_fields.length === 0 && (
                  <p className="text-xs text-slate-500">Click above to open the PDF and place signature fields where workers/supervisors need to sign. Or skip to use a general signature pad.</p>
                )}
                {showFieldPlacer && (
                  <PDFSignatureFieldPlacer
                    fileUrl={docForm.file_url}
                    signatureFields={docForm.signature_fields}
                    onFieldsChange={(fields) => setDocForm(prev => ({ ...prev, signature_fields: fields }))}
                    users={users}
                    jobAssignedUsers={assignedUsers}
                  />
                )}
              </div>
            )}

            <div>
              <label className="text-xs text-slate-500 uppercase">When to Sign</label>
              <select
                value={docForm.signing_frequency}
                onChange={(e) => setDocForm(prev => ({ ...prev, signing_frequency: e.target.value }))}
                className="w-full mt-1 px-3 py-2 border-2 rounded-md"
              >
                {Object.entries(frequencyLabels).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>
            
            {/* Who Needs to Sign */}
            <div className="border-2 border-slate-200 rounded-lg p-4 space-y-4">
              <h5 className="font-bold uppercase text-sm text-slate-600">Who Needs to Sign</h5>
              
              {/* Supervisor */}
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={docForm.requires_supervisor}
                  onChange={(e) => setDocForm(prev => ({ ...prev, requires_supervisor: e.target.checked }))}
                  className="w-5 h-5"
                />
                <span className="font-medium">Supervisor/Manager</span>
              </label>
              
              {/* All Employees */}
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-100">
                <input
                  type="checkbox"
                  checked={docForm.all_employees_must_sign}
                  onChange={(e) => setDocForm(prev => ({ 
                    ...prev, 
                    all_employees_must_sign: e.target.checked,
                    required_employee_ids: [],
                    required_roles: []
                  }))}
                  className="w-5 h-5"
                />
                <span className="font-medium">All Employees on Job</span>
              </label>
              
              {!docForm.all_employees_must_sign && (
                <>
                  {/* By Role */}
                  <div className="border-t pt-3">
                    <p className="text-sm font-medium text-slate-600 mb-2">Or by Role:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(roleLabels).map(([role, label]) => (
                        <label key={role} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          docForm.required_roles.includes(role) ? 'bg-orange-100 border-2 border-orange-400' : 'bg-slate-100 border-2 border-transparent'
                        }`}>
                          <input
                            type="checkbox"
                            checked={docForm.required_roles.includes(role)}
                            onChange={() => toggleRole(role)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Specific Employees */}
                  <div className="border-t pt-3">
                    <p className="text-sm font-medium text-slate-600 mb-2">Or Specific Employees:</p>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                      {assignedUsers.length > 0 ? assignedUsers.map(emp => (
                        <label key={emp.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                          docForm.required_employee_ids.includes(emp.id) ? 'bg-blue-100 border-2 border-blue-400' : 'bg-slate-100 border-2 border-transparent'
                        }`}>
                          <input
                            type="checkbox"
                            checked={docForm.required_employee_ids.includes(emp.id)}
                            onChange={() => toggleEmployee(emp.id)}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">{emp.first_name} {emp.last_name}</span>
                        </label>
                      )) : (
                        <p className="text-sm text-slate-500 col-span-2">No workers assigned to this job yet</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Save as Template */}
            <label className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200 cursor-pointer hover:bg-amber-100 transition-colors">
              <input
                type="checkbox"
                checked={saveAsTemplate}
                onChange={(e) => setSaveAsTemplate(e.target.checked)}
                className="w-5 h-5 rounded border-2 border-amber-400 text-amber-500"
                data-testid="save-as-template-checkbox"
              />
              <div>
                <span className="font-bold text-amber-900 text-sm">Save as Template</span>
                <p className="text-xs text-amber-700">Reuse this document setup on other jobs with one click</p>
              </div>
            </label>
            
            <div className="flex gap-2">
              <Button onClick={handleCreateDoc} data-testid="submit-signing-doc">Add Document</Button>
              <Button variant="ghost" onClick={() => setShowAddDoc(false)}>Cancel</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Documents List */}
      {documents.length > 0 ? (
        <div className="space-y-3">
          {documents.map(doc => (
            <Card key={doc.id} className="border-2">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-5 h-5 text-orange-500" />
                    <h4 className="font-bold">{doc.name}</h4>
                  </div>
                  {doc.description && <p className="text-sm text-slate-600 mb-2">{doc.description}</p>}
                  
                  <div className="flex flex-wrap gap-2 text-xs">
                    <Badge variant="info">{frequencyLabels[doc.signing_frequency] || doc.signing_frequency}</Badge>
                    {doc.signature_fields?.length > 0 && (
                      <Badge variant="warning">{doc.signature_fields.length} Signature Spot(s)</Badge>
                    )}
                    {doc.requires_supervisor && <Badge variant="default">Supervisor Required</Badge>}
                    {doc.all_employees_must_sign && <Badge variant="default">All Employees</Badge>}
                    {doc.required_roles?.map(r => <Badge key={r} variant="default">{roleLabels[r] || r}</Badge>)}
                    {doc.required_employee_ids?.length > 0 && (
                      <Badge variant="default">{doc.required_employee_ids.length} Specific Employee(s)</Badge>
                    )}
                  </div>
                  
                  <p className="text-xs text-slate-400 mt-2">
                    {doc.signature_count || 0} signature(s) collected
                  </p>
                </div>
                
                <div className="flex flex-col gap-2">
                  <a 
                    href={doc.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-3 py-1 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" /> View Original
                  </a>
                  <button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/jobs/${job.id}/signing-documents/${doc.id}/signed-pdf`, { responseType: 'blob' });
                        const blob = new Blob([res.data], { type: 'application/pdf' });
                        const url = URL.createObjectURL(blob);
                        window.open(url, '_blank');
                      } catch (e) {
                        toast.error('Failed to download signed PDF');
                      }
                    }}
                    className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors flex items-center gap-1"
                    data-testid={`download-signed-pdf-${doc.id}`}
                  >
                    <Download className="w-4 h-4" /> Signed PDF
                  </button>
                  <button 
                    onClick={() => { setSelectedDoc(doc); setShowSignatures(true); }}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors flex items-center gap-1"
                  >
                    <FileText className="w-4 h-4" /> Signatures
                  </button>
                  {canEdit && (
                    <button 
                      onClick={() => handleDeleteDoc(doc.id)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors flex items-center gap-1"
                    >
                      <Trash2 className="w-4 h-4" /> Remove
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg">
          <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="font-semibold">No signing documents yet</p>
          <p className="text-sm">Add documents that employees need to sign when clocking in</p>
        </div>
      )}

      {/* Signatures Modal */}
      {showSignatures && selectedDoc && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{selectedDoc.name} - Signatures</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowSignatures(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            {selectedDoc.signatures?.length > 0 ? (
              <div className="space-y-3">
                {selectedDoc.signatures.map(sig => (
                  <div key={sig.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">{sig.user_name}</p>
                      <p className="text-sm text-slate-500 capitalize">{sig.user_role?.replace('_', ' ')}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-green-600 font-medium">Signed</p>
                      <p className="text-xs text-slate-400">{new Date(sig.signed_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 py-8">No signatures collected yet</p>
            )}
          </Card>
        </div>
      )}

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-y-auto" data-testid="template-picker-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg uppercase">Document Templates</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowTemplatePicker(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Pick a template to add it to <strong>{job?.title}</strong> with one click.
            </p>
            
            {loadingTemplates ? (
              <div className="text-center py-8 text-slate-400">Loading templates...</div>
            ) : templates.length > 0 ? (
              <div className="space-y-3">
                {templates.map(tpl => (
                  <div key={tpl.id} className="p-4 border-2 rounded-lg hover:border-orange-300 transition-colors bg-white" data-testid={`template-${tpl.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-sm">{tpl.name}</h4>
                        {tpl.description && <p className="text-xs text-slate-500 mt-0.5">{tpl.description}</p>}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium">
                            {frequencyLabels[tpl.signing_frequency] || tpl.signing_frequency}
                          </span>
                          {tpl.signature_fields?.length > 0 && (
                            <span className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                              {tpl.signature_fields.length} sig spot(s)
                            </span>
                          )}
                          {tpl.requires_supervisor && (
                            <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">Supervisor</span>
                          )}
                          {tpl.all_employees_must_sign && (
                            <span className="text-[10px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">All Employees</span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">
                          Used {tpl.usage_count || 0} time(s) &middot; by {tpl.created_by_name}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        <Button size="sm" onClick={() => handleUseTemplate(tpl)} data-testid={`use-template-${tpl.id}`}>
                          <Plus className="w-3.5 h-3.5 mr-1" /> Use
                        </Button>
                        <button
                          onClick={() => handleDeleteTemplate(tpl.id)}
                          className="text-[10px] text-red-500 hover:text-red-700 px-2 py-1"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-400">
                <FileText className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No templates yet</p>
                <p className="text-xs mt-1">Add a document with "Save as Template" checked to create one</p>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};


// Job Modal Component

export default SigningDocumentsView;
